import { endsWith, isString } from 'lodash-es';
import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import { useSearchParam } from 'react-use';
import { ReplaySubject, type Observable } from 'rxjs';
import type { DefaultTheme } from 'styled-components';
import { useConstant } from '../hooks/useConstant';
import { useSessionStorage } from '../hooks/useSessionStorage';
import { themes } from '../styles';
import { hashThemeRunTime } from '../styles/themes';
import { CustomerUserConfigLayoutType } from '../types/CustomerUserConfigLayout';
import type { WLOrgConfig } from '../types/WLOrgConfig';
import { logger } from '../utils';
import { envConfig } from '../utils/envConfig';
import { Get } from '../utils/http';
import { getWLOrgConfigUrl } from './getWLOrgConfigUrl';

const DEFAULT_WL_FLAGS: Omit<
  WLOrgConfig,
  'title' | 'theme' | 'favicon' | 'layout' | 'logo' | 'supportPhone' | 'supportEmail' | 'supportWebsite'
> = {
  // [sc-76773] Enable Customer Withdraw on the "default" whitelabel UI config
  enableBalanceTransactions: true,
  blotterRowsMax: 10000,
  disableCustomLadderSizeBuckets: false,
  hideCFDLabels: false,
  hideRecordDepositButton: false,
  showMarketAccountDisplayName: false,
  usePreciseTopOfBookIncrement: true,
  allowMobile: true,
  isFallbackConfig: true,
  favoredPairCurrency: 'USD',
  useTradingView: false,
  hideTradingViewVolume: false,
  userCanModifyHighTouchOrders: true,
};

export const DEFAULT_WL_ORG_CONFIG: WLOrgConfig = {
  title: 'Demo Trade',
  theme: 'DarkTheme',
  favicon: 'cryptoco-otc-icon.svg',
  layout: CustomerUserConfigLayoutType.TradingLayout,
  supportPhone: 'support-phone-here',
  supportEmail: 'support-email-here',
  supportWebsite: 'support-website-here',
  logo: {
    dark: 'cryptoco-otc-logo-d.svg',
    light: 'cryptoco-otc-logo-l.svg',
  },
  ...DEFAULT_WL_FLAGS,
};

export interface WLOrgConfigContextProps {
  config: WLOrgConfig;
  isLoaded: boolean;
  overrides: Partial<WLOrgConfig>;
  loadedConfig: Observable<WLOrgConfig>;
  setOverrides: (overrides: Partial<WLOrgConfig>) => void;
}

export const WLCustomerConfigContext = createContext<WLOrgConfigContextProps | undefined>(undefined);

export function useWLOrgConfigContext() {
  const context = useContext(WLCustomerConfigContext);
  if (context === undefined) {
    throw new Error('Missing WLCustomerConfigContext.Provider further up in the tree. Did you forget to add it?');
  }
  return context;
}

export function getThemeFromConfig(config: WLOrgConfig): DefaultTheme {
  return (themes[config.theme as keyof typeof themes] ?? themes[hashThemeRunTime('DarkTheme')]) as DefaultTheme;
}

const DEMO_OVERRIDES = 'DEMO_OVERRIDES';

export const WLOrgConfigProvider = ({ children }: PropsWithChildren) => {
  const urlOverride = useSearchParam('override');
  const urlthemeOverride = useSearchParam('themeOverride');
  // The usecase here is to provide a way to test customer themes using url params in local/staging.
  // Try it out by adding ?themeOverride=DarkTDATheme to the end of the url.
  const isMobile = import.meta.env.VITE_IS_MOBILE === 'true';
  // URL query overrides are web-only; mobile has no address bar to set them.
  const { override, themeOverride } =
    !isMobile && (envConfig.VITE_AVA_ENV === 'local' || envConfig.VITE_AVA_ENV === 'staging')
      ? { override: urlOverride, themeOverride: urlthemeOverride }
      : { override: undefined, themeOverride: undefined };
  const [config, setConfig] = useState<WLOrgConfig>(DEFAULT_WL_ORG_CONFIG);
  const [overrides, setOverrides] = useSessionStorage<Partial<WLOrgConfig>>(DEMO_OVERRIDES, {});
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadedConfig] = useState(() => new ReplaySubject<WLOrgConfig>(1));

  const canUseDefaultConfig = useConstant(
    // Allow using default customer config in a Local environment,
    // or on NON-PROD environments that end with ".whitelabel-talos.com",
    // or on mobile: Capacitor serves from the customer hostname, so a failed org-config
    // fetch must not hang forever (Intl waits on loadedConfig → blank white screen).
    isMobile ||
      envConfig.VITE_AVA_ENV === 'local' ||
      (envConfig.VITE_AVA_ENV !== 'prod' && endsWith(window.location.hostname, '.whitelabel-talos.com'))
  );

  useEffect(() => {
    const configUrl = getWLOrgConfigUrl({
      isMobile,
      isTestEnvironment: import.meta.env.VITE_IS_TEST_ENVIRONMENT === 'true',
      baseUrl: envConfig.VITE_BASE_URL,
      override,
      hostname: window.location.hostname,
    });
    const fetchConfig = async (shouldRetry: boolean) => {
      logger.info(`[config] Fetching customer config`, { extra: { url: configUrl } });
      // [DEAL-5063] If we can fallback to the default config, we can skip the missing config file error
      return Get(configUrl, '', {
        skippedErrorReportingStatusCodes: canUseDefaultConfig ? [404] : undefined,
      })
        .then(async (res: WLOrgConfig) => {
          if (envConfig.VITE_AVA_ENV === 'local' && isString(res as unknown)) {
            // [DEAL-5218] the res is actually a html document, so throw it away and use the default
            const defaultConfig = {
              ...DEFAULT_WL_ORG_CONFIG,
              theme: hashThemeRunTime(themeOverride ?? DEFAULT_WL_ORG_CONFIG.theme),
            };
            setConfig(defaultConfig);
            loadedConfig.next(defaultConfig);
            setIsLoaded(true);
            logger.info(`[config] Defaulted to default org config`, {
              extra: { loadedConfig: defaultConfig },
            });
            return;
          }
          const config: WLOrgConfig = {
            ...DEFAULT_WL_FLAGS,
            ...res,
            volumeLadderDecimalPrecision: res.volumeLadderDecimalPrecision?.toString(),
            theme: hashThemeRunTime(themeOverride ?? res.theme),
          };
          setConfig(config);
          loadedConfig.next(config);
          setIsLoaded(true);
          logger.info(`[config] Loaded customer config`, { extra: { url: configUrl, loadedConfig: config } });
        })
        .catch(reason => {
          if (shouldRetry) {
            fetchConfig(false);
          } else if (canUseDefaultConfig) {
            logger.error(new Error(`Could not load customer config ${configUrl}, using default`), {
              extra: { reason: String(reason), isMobile },
            });
            setConfig(DEFAULT_WL_ORG_CONFIG);
            loadedConfig.next(DEFAULT_WL_ORG_CONFIG);
            setIsLoaded(true);
          } else {
            throw new Error(`Could not load customer config ${configUrl}: ${reason}`);
          }
        });
    };
    fetchConfig(true);
  }, [canUseDefaultConfig, override, themeOverride, loadedConfig, isMobile]);

  useEffect(() => {
    if (isLoaded) {
      document.title = config.title || '';
      const favicon = document.getElementById('favicon') as HTMLLinkElement | undefined;
      if (favicon) {
        favicon.href = `customers/${config.favicon}`;
      }
    }
  }, [config, isLoaded]);

  const value = useMemo(() => {
    return {
      config: { ...config, ...overrides },
      isLoaded,
      loadedConfig,
      overrides,
      setOverrides,
    };
  }, [config, isLoaded, overrides, setOverrides, loadedConfig]);

  return <WLCustomerConfigContext.Provider value={value}>{children}</WLCustomerConfigContext.Provider>;
};
